import * as React from "react";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link } from "react-router-dom";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Form, useFormApi } from "informed";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { StudyForm, ExtractBase64Image, StudyFormComponent } from "./StudyForm";
import { StudyOverreadForm, OverreadForm } from "./StudyOverreadForm";
import { StudyMetricsForm } from "./StudyMetricsForm";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
    return (
        <div className="h2 pull-right">
            <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                Save
            </button>&nbsp;
            <Link className="btn btn-primary btn-outline" to="/studies" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                Return to List
            </Link>
        </div>
    );
}

export const CreateStudyForm = () => {
    const client = useWebPortalApiClient();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [logoToUpload, setLogoToUpload] = React.useState<File>();
    const formApi = useFormApi<StudyForm & OverreadForm>();
    const legacyClient = useWebPortalLegacyClient();

    const onSubmit = async () => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }


        try {
            let base64StringLogo;
            if (logoToUpload) {
                base64StringLogo = await ExtractBase64Image(logoToUpload);
            }

            const study = await client.client.postStudy({
                name: state.values.Name,
                protocol: state.values.Protocol,
                sessionTypeIds: state.values.SessionTypes?.map(st => st.id),
                countriesIds: state.values.Countries.map(c => c.id),
                languageIds: state.values.DiaryLanguage?.map(l => l.localeName),
                deviceTypeIds: state.values.DeviceTypes.map(dt => dt.id),
                description: state.values.Description,
                targetSubjects: state.values.TargetSubjects,
                targetSessions: state.values.TargetSessions,
                logo: base64StringLogo,
                logoMediaType: logoToUpload?.type,
                vitalographContactEmail: state.values.StudyContactEmail,
                welcomeMessage: state.values.WelcomeMessage
            });

            if (study && state.values.ChallengeNotificationEmail) {
                await legacyClient.putStudySetting(study.id, "Vitalograph.WebPortal.Data.Settings.Dlco.OverreadingChallengeNotificationEmailSetting", state.values.ChallengeNotificationEmail);
            }
            

            if (study) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setErrorAlert(ex.message !== undefined ? ex.message : ex.Message);
        }

        setSubmitting(false);
    }

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Study</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Core Details</h4>
                        </div>
                        <div className="panel-body">
                            <StudyFormComponent onAlert={(alert) => setErrorAlert(alert)} onFileAttached={(file) => setLogoToUpload(file)} />
                        </div>
                    </div>
                    <div id="overreadSetupPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Over-Read Setup</h4>
                        </div>
                        <div className="panel-body">
                            <StudyOverreadForm />
                        </div>
                    </div>
                    <div id="metricsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Metrics</h4>
                        </div>
                        <div className="panel-body">
                            <StudyMetricsForm />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
        </>
    );
}

export const CreateStudyScreen = () => {
    const { currentUserPermissions } = useSecurityContext();

    return (
        <DocumentTitle title="Create Study | Vitalograph Web Portal">
            <Form noValidate>
                <CreateStudyForm />
            </Form>
        </DocumentTitle>
    );
}
